import { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import {
  EMPTY_OBJECT,
  INPUT_AUTOCOMPLETE_VALUE,
} from '../../../../constants/common'
import { useScreen } from '../../../../hooks'
import { stopPropagation } from '../../../../utils/events'
import { DragDropDroppableArea } from '../../../DragDrop'
import { InputField } from '../../../Input'
import { useSelectContext, useSelectFocusContext } from '../../../Select'
import { MAX_HEIGHT_FOR_SHOW_MORE_BTN } from '../EmailRecipientsField.constants'
import { RecipientsInput } from '../RecipientsInput'
import { SelectButtonActions } from '../SelectButtonActions'
import { ShowContentButton } from '../ShowContentButton'

import { BUTTON_HEIGHT } from './SelectButton.constants'
import { getMaxWidth } from './SelectButton.utils'

import * as styles from './SelectButton.module.css'

/**
 * @property {boolean} [blocked]
 * blocked: behaves like disabled (no clicks, no value changes),
 * but keeps the normal visual style (no opacity change) and
 * shows a "not-allowed" cursor. Unlike "readonly", it should not
 * handle clicks or focus changes at all.
 *
 * @property {boolean} [readonly]
 * readonly: value cannot be changed but the control can still receive focus
 */
function SelectButton({
  actionsProps,
  blocked,
  droppableIndex,
  errorText,
  initShowMoreValue,
  inputProps: { onBlur, onPaste, ...inputProps } = EMPTY_OBJECT,
  inputValue,
  isDropDisabled,
  name,
  placeholder,
  readonly,
  ref,
  selectedOptionsProps,
  showError,
  value,
  onClearAll,
  onClick,
  onDeleteOption,
  onKeyDown,
  ...restProps
}) {
  const [showMore, setShowMore] = useState(initShowMoreValue)
  const [optionsHeight, setOptionsHeight] = useState(0)

  const { disabled } = useSelectContext()

  const { focused, handleBlur, handleFocus } = useSelectFocusContext()

  const { isMobileScreenSize } = useScreen() // TODO make same by css media instead of useScreen

  const actionsRef = useRef(null)
  const optionsRef = useRef(null)

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      if (optionsRef.current) {
        setOptionsHeight(optionsRef?.current?.scrollHeight || 0)
      }
    })

    ro.observe(optionsRef.current)

    return () => {
      ro.disconnect()
    }
  }, [value])

  // Reads the actions element width to constrain the input's first-row max
  // width; re-evaluated on every render (focus/value changes) so it stays in
  // sync. Falls back to a constant until the node mounts.
  // eslint-disable-next-line react-hooks/refs
  const maxWidth = getMaxWidth(actionsRef.current)

  const inputFieldClassName = classNames(styles.inputField)

  const inputClassName = classNames(styles.input, {
    [styles.emptyField]: value.length === 0,
    [styles.hiddenInput]: !focused && value.length > 0,
    [styles.withValue]: focused && value.length > 0,
  })

  const handleShowMore = useCallback(e => {
    stopPropagation(e)

    setShowMore(val => !val)
  }, [])

  const handleClickContainer = useCallback(
    e => {
      onClick?.(e)

      if (disabled || blocked) {
        return
      }

      if (!focused) {
        handleFocus()
      }
    },
    [blocked, disabled, focused, handleFocus, onClick]
  )

  return (
    <div
      {...restProps}
      ref={ref}
      onBlur={handleBlur}
      onClick={handleClickContainer}
    >
      <DragDropDroppableArea
        className={styles.droppable}
        data={{ droppableIndex }}
        disabled={isDropDisabled}
        id={name}
      >
        <InputField
          Component={RecipientsInput}
          afterButton={
            optionsHeight > MAX_HEIGHT_FOR_SHOW_MORE_BTN && !focused ? (
              <ShowContentButton open={!showMore} onClick={handleShowMore} />
            ) : null
          }
          autoComplete={INPUT_AUTOCOMPLETE_VALUE.off}
          beforeButton={
            <SelectButtonActions
              {...actionsProps}
              isOpen={focused}
              ref={actionsRef}
              onClickOpen={handleFocus}
            />
          }
          blocked={blocked}
          borderless={!isMobileScreenSize}
          className={inputFieldClassName}
          disabled={disabled}
          errorText={errorText}
          height={BUTTON_HEIGHT}
          inputProps={{
            ...inputProps,
            autoFocus: true,
            className: inputClassName,
            droppableIndex,
            maxHeight:
              showMore && !focused ? MAX_HEIGHT_FOR_SHOW_MORE_BTN : null,
            maxWidth,
            name,
            optionsRef,
            readonly,
            selectedOptionsProps,
            onClearAll,
            onDeleteOption,
          }}
          name={name}
          placeholder={placeholder}
          showError={showError}
          value={inputValue}
          wrapperClassName={styles.SelectButton}
          onBlur={onBlur}
          onClick={handleClickContainer}
          onClickBefore={handleClickContainer}
          onFocus={handleClickContainer}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </DragDropDroppableArea>
    </div>
  )
}

export default SelectButton
